import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FileDown } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServices,
  usePocServices,
  useDeviceLiquidationServices,
  type ServiceRow,
  type ServiceStatementRow,
} from '@/api/services';
import { exportServicesExcel } from '@/api/services-mutations';
// The contract list section is generalized over the row type + query hook — reused here.
import { ContractListSection } from '@/pages/contracts/components/contract-list-section';
import { ServiceStatBand } from './components/service-stat-band';
import { WaitingConfirmListSection } from './list-sections/waiting-confirm-list-section';
import { StatementListSection } from './list-sections/statement-list-section';
import { SlaServicesListSection } from './list-sections/sla-services-list-section';
import { serviceColumns, pocServiceColumns } from './list-configs/service-column-definitions';
import { serviceFilters, serviceStatusOnlyFilters } from './list-configs/service-filter-fields';

/** Tab definitions — value drives `?tab=`, each has its own feed hook + columns. */
const TABS = [
  { value: 'all', label: 'All services' },
  { value: 'waiting-confirm', label: 'Waiting confirmation' },
  { value: 'poc', label: 'POC' },
  { value: 'device-liquidation', label: 'Device liquidation' },
  { value: 'sla', label: 'Services with SLA' },
  { value: 'statement', label: 'Statement' },
] as const;

type TabValue = (typeof TABS)[number]['value'];

/**
 * Services — the ported DataCentral v4 service manager. A `PageHeader` over a
 * `Tabs` switcher (All · Waiting confirmation · POC · Device liquidation ·
 * Services with SLA · Statement) persisted in `?tab=`. The All tab carries a
 * status stat band and an Excel export button. Statement and SLA tabs have
 * their own dedicated list sections.
 */
export default function ServicesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);

  const urlTab = searchParams.get('tab');
  const activeTab: TabValue = TABS.some((t) => t.value === urlTab)
    ? (urlTab as TabValue)
    : 'all';

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const openDetail = (row: ServiceRow) => navigate(`/services/${row.service_id}`);
  const openStatementDetail = (row: ServiceStatementRow) => navigate(`/services/${row.service_id}`);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportServicesExcel();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      a.href = url;
      a.download = `services-${dateStr}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export services');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container size="full">
      <PageHeader
        title="Services"
        description="Every provisioned service across its lifecycle — active, awaiting confirmation, trials and liquidation."
        actions={
          activeTab === 'all' ? (
            <Button
              variant="secondary"
              startIcon={<FileDown size={15} />}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          ) : undefined
        }
      />

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all">
          <ServiceStatBand />
          <ContractListSection<ServiceRow>
            useListQuery={useServices}
            columns={serviceColumns}
            getRowId={(r) => String(r.service_id)}
            onRowClick={openDetail}
            filterFields={serviceFilters}
            searchPlaceholder="Search services…"
            errorTitle="Failed to load services"
            emptyTitle="No services found"
          />
        </TabsContent>

        <TabsContent value="waiting-confirm">
          <WaitingConfirmListSection onRowClick={openDetail} />
        </TabsContent>

        <TabsContent value="poc">
          <ContractListSection<ServiceRow>
            useListQuery={usePocServices}
            columns={pocServiceColumns}
            getRowId={(r) => String(r.service_id)}
            onRowClick={openDetail}
            filterFields={serviceStatusOnlyFilters}
            searchPlaceholder="Search POC services…"
            errorTitle="Failed to load POC services"
            emptyTitle="No POC services found"
          />
        </TabsContent>

        <TabsContent value="device-liquidation">
          <ContractListSection<ServiceRow>
            useListQuery={useDeviceLiquidationServices}
            columns={serviceColumns}
            getRowId={(r) => String(r.service_id)}
            onRowClick={openDetail}
            filterFields={serviceStatusOnlyFilters}
            searchPlaceholder="Search device-liquidation services…"
            errorTitle="Failed to load services"
            emptyTitle="No device-liquidation services found"
          />
        </TabsContent>

        <TabsContent value="sla">
          <SlaServicesListSection />
        </TabsContent>

        <TabsContent value="statement">
          <StatementListSection onRowClick={openStatementDetail} />
        </TabsContent>
      </Tabs>
    </Container>
  );
}
